import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '../useApiMutation';
import { operationsClient, apiGet, apiPost } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { bookingKeys, schedulerKeys } from '@/lib/queryKeys';

/**
 * Optimize one day's route — by a single technician OR a whole crew (routed as
 * a unit). Exactly one of technicianId / crewId is set. The result's
 * technicianId is the crew's lead, which apply/undo use unchanged.
 */
export interface RouteOptimizeRequest {
  technicianId?: string;
  crewId?: string;
  date: string;
}

/** Apply carries the plan it is committing — see RouteOptimizeResult.planToken.
 *  Always by the resolved lead technician (from the optimize result). */
export interface RouteApplyRequest {
  technicianId: string;
  date: string;
  planToken: string;
}

export interface RouteOptimizedStop {
  appointmentId: string;
  newStartAt: string;
  newEndAt: string;
  driveMinutesFromPrev: number;
  moved: boolean;
  material: boolean;
}

export interface RouteOptimizeResult {
  technicianId: string;
  date: string;
  alreadyOptimal: boolean;
  beforeDriveMinutes: number;
  afterDriveMinutes: number;
  savedMinutes: number;
  customerNotifyCount: number;
  orderedStops: RouteOptimizedStop[];
  unroutable: Array<{ appointmentId: string; reason: string }>;
  /**
   * Opaque signed handoff of THIS plan — pass it straight back to
   * useApplyRoute. Apply commits exactly the order shown here instead of
   * re-solving, so the dispatcher can never commit (and text customers about)
   * a plan they never reviewed. Null when there is nothing to apply.
   */
  planToken: string | null;
  /**
   * Present only when optimizing a CREW — the roster (so the preview shows the
   * whole crew, not just the lead) and the member commitments the route was
   * planned around (a helper's own job elsewhere → an immovable hold).
   */
  crew?: {
    members: Array<{
      technicianId: string;
      name: string;
      role: 'LEAD' | 'HELPER';
    }>;
    holds: Array<{
      technicianId: string;
      technicianName: string;
      label: string;
      startAt: string;
      endAt: string;
    }>;
  };
}

export interface RouteUndoEntry {
  appointmentId: string;
  previousStartAt: string | null;
  dayFlex: boolean;
}

export interface RouteApplyResult {
  applied: number;
  planned: number;
  skipped: number;
  alreadyOptimal: boolean;
  /** Coordinate-less day-flex visits sequenced at the tail of the day. */
  tailPlaced: number;
  /** Visits the optimizer could not place (e.g. address not geocoded). */
  unroutable: Array<{ appointmentId: string; reason: string }>;
  /** Opaque signed pre-image token — pass back to useUndoRoute to revert. */
  undoToken: string | null;
  /** Pre-image of the moved visits; retained only for local summary state. */
  undo: RouteUndoEntry[];
}

export interface RouteStopView {
  appointmentId: string;
  lat: number;
  lng: number;
  startAt: string | null;
  endAt: string | null;
  customerName: string;
  pinned: boolean;
}

export interface RouteView {
  technicianId: string;
  date: string;
  stops: RouteStopView[];
  /** §9.5 — the real street polyline (Google encoded) for the day's route, or
   *  null when none has been computed yet. */
  encodedPolyline?: string | null;
}

export interface RouteSummaryView {
  technicianId: string;
  technicianName: string;
  crewName: string | null;
  territoryId: string | null;
  territoryName: string | null;
  stopCount: number;
  firstStart: string | null;
  lastEnd: string | null;
  bookedMinutes: number;
  capacityMinutes: number;
  driveMinutes: number;
  distanceMeters: number;
}

export interface RouteSummaryFilters {
  technicianId?: string;
  territoryId?: string;
  jobTypeId?: string;
  // NOT-542 — a BU id, or the 'unassigned' sentinel; mirrors the dispatch board.
  businessUnitId?: string;
  priority?: string;
  hasExceptions?: boolean;
}

/** Propose an optimized order for a tech's day (does not apply). */
export function useOptimizeRoute() {
  return useApiMutation<RouteOptimizeResult, RouteOptimizeRequest>({
    mutationFn: (dto) =>
      apiPost<RouteOptimizeResult, RouteOptimizeRequest>(
        operationsClient,
        schedulerEndpoints.routeOptimize(),
        dto,
      ),
    showErrorToast: true,
  });
}

/** Publish flexible ETAs and apply any timed-stop reorder. */
export function useApplyRoute() {
  const queryClient = useQueryClient();
  return useApiMutation<RouteApplyResult, RouteApplyRequest>({
    mutationFn: (dto) =>
      apiPost<RouteApplyResult, RouteApplyRequest>(
        operationsClient,
        schedulerEndpoints.routeApply(),
        dto,
      ),
    showErrorToast: true,
    onSuccess: () => {
      // Invalidate the whole scheduler namespace, not just `bookings()` (no
      // params): the board/calendar fetch under `bookings({ dateFrom, … })` and
      // the param-less key doesn't prefix-match that (mirrors useMoveBooking).
      // The §15 unsequenced tray relies on this refetch to turn newly-timed
      // DAY_FLEXIBLE rows into timeline tiles.
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.all() });
      void queryClient.invalidateQueries({ queryKey: bookingKeys.all() });
      // Route polyline + summaries key on literal 'route'/'route-summaries', not
      // the tenant prefix, so schedulerKeys.all() misses them — refresh both.
      void queryClient.invalidateQueries({ queryKey: ['scheduler', 'route'] });
      void queryClient.invalidateQueries({
        queryKey: ['scheduler', 'route-summaries'],
      });
    },
  });
}

export interface RouteUndoRequest {
  technicianId: string;
  date: string;
  undoToken: string;
}

export interface RouteUndoResult {
  reverted: number;
  skipped: number;
}

/** Revert a just-applied optimization (restore each visit's prior time). */
export function useUndoRoute() {
  const queryClient = useQueryClient();
  return useApiMutation<RouteUndoResult, RouteUndoRequest>({
    mutationFn: (dto) =>
      apiPost<RouteUndoResult, RouteUndoRequest>(
        operationsClient,
        schedulerEndpoints.routeApplyUndo(),
        dto,
      ),
    showErrorToast: true,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.all() });
      void queryClient.invalidateQueries({ queryKey: bookingKeys.all() });
      // Route polyline + summaries key on literal 'route'/'route-summaries', not
      // the tenant prefix, so schedulerKeys.all() misses them — refresh both.
      void queryClient.invalidateQueries({ queryKey: ['scheduler', 'route'] });
      void queryClient.invalidateQueries({
        queryKey: ['scheduler', 'route-summaries'],
      });
    },
  });
}

export interface RouteRejectRequest {
  technicianId: string;
  date: string;
  savedMinutes: number;
}

/**
 * Signal that the dispatcher dismissed a proposed optimization (§21.4). Pure
 * analytics — no scheduling change — so it never toasts on error.
 */
export function useRejectRoute() {
  return useApiMutation<{ rejected: true }, RouteRejectRequest>({
    mutationFn: (dto) =>
      apiPost<{ rejected: true }, RouteRejectRequest>(
        operationsClient,
        schedulerEndpoints.routeReject(),
        dto,
      ),
    showErrorToast: false,
  });
}

// ── §8.3 fleet (multi-tech) optimization ───────────────────────────────────

export interface FleetMoveView {
  appointmentId: string;
  customerName: string;
  fromTechnicianId: string;
  toTechnicianId: string;
  newStartAt: string;
  newEndAt: string;
  material: boolean;
}

export interface FleetPerTech {
  technicianId: string;
  technicianName: string;
  beforeDriveMinutes: number;
  afterDriveMinutes: number;
  stopCountBefore: number;
  stopCountAfter: number;
}

export interface FleetOptimizeResult {
  date: string;
  alreadyOptimal: boolean;
  beforeDriveMinutes: number;
  afterDriveMinutes: number;
  savedMinutes: number;
  customerNotifyCount: number;
  moves: FleetMoveView[];
  perTech: FleetPerTech[];
  unroutable: Array<{ appointmentId: string; reason: string }>;
}

export interface FleetApplyResult {
  applied: number;
  skipped: number;
  alreadyOptimal: boolean;
  results: Array<{ appointmentId: string; ok: boolean; error?: string }>;
}

export interface FleetRequest {
  technicianIds: string[];
  date: string;
}

/** Propose a multi-tech reassignment plan (preview, no commit). */
export function useOptimizeFleet() {
  return useApiMutation<FleetOptimizeResult, FleetRequest>({
    mutationFn: (dto) =>
      apiPost<FleetOptimizeResult, FleetRequest>(
        operationsClient,
        schedulerEndpoints.routeOptimizeFleet(),
        dto,
      ),
    showErrorToast: true,
  });
}

/** Apply an accepted fleet plan (gated routes:accept_fleet_plan on the server). */
export function useAcceptFleetPlan() {
  const queryClient = useQueryClient();
  return useApiMutation<FleetApplyResult, FleetRequest>({
    mutationFn: (dto) =>
      apiPost<FleetApplyResult, FleetRequest>(
        operationsClient,
        schedulerEndpoints.routeAcceptPlan(),
        dto,
      ),
    showErrorToast: true,
    onSuccess: () => {
      // Same as useApplyRoute — the param-less bookings() key doesn't match the
      // parameterized board/calendar queries; invalidate the namespace.
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.all() });
      void queryClient.invalidateQueries({ queryKey: bookingKeys.all() });
      // Route polyline + summaries key on literal 'route'/'route-summaries', not
      // the tenant prefix, so schedulerKeys.all() misses them — refresh both.
      void queryClient.invalidateQueries({ queryKey: ['scheduler', 'route'] });
      void queryClient.invalidateQueries({
        queryKey: ['scheduler', 'route-summaries'],
      });
    },
  });
}

/** Current ordered route for a tech/day — drives the dispatch map polyline. */
export function useRoute(technicianId: string | undefined, date: string, enabled = true) {
  return useQuery<RouteView>({
    queryKey: ['scheduler', 'route', technicianId, date],
    queryFn: () =>
      apiGet<RouteView>(operationsClient, schedulerEndpoints.route(technicianId!, date)),
    enabled: enabled && Boolean(technicianId),
  });
}

/** One row per technician route for Dispatch → Routes. */
export function useRouteSummaries(date: string, filters: RouteSummaryFilters = {}, enabled = true) {
  return useQuery<RouteSummaryView[]>({
    queryKey: ['scheduler', 'route-summaries', date, filters],
    queryFn: () =>
      apiGet<RouteSummaryView[]>(operationsClient, schedulerEndpoints.routeSummaries(), {
        params: {
          date,
          technicianId: filters.technicianId,
          territoryId: filters.territoryId,
          jobTypeId: filters.jobTypeId,
          businessUnitId: filters.businessUnitId,
          priority: filters.priority,
          hasExceptions:
            filters.hasExceptions === undefined ? undefined : String(filters.hasExceptions),
        },
      }),
    enabled: enabled && Boolean(date),
  });
}
